import {
  FloatingArrow,
  arrow,
  autoPlacement,
  autoUpdate,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react';
import Icon from '@mdi/react';
import { Dialog, DialogContent, DialogTrigger, pdsTv } from '@purinanbm/pds-ui';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import React, { useEffect } from 'react';
import { mdiClose } from 'src/assets/icons/mdiIcons';
import { IInnerProps } from '../types';
import { HotspotContent } from './HotspotContent';
import { HotspotTrigger } from './HotspotTrigger';

const dialogContentTv = pdsTv({
  // Ripped from @pds-ui.
  base: [
    'pds-relative',
    'pds-overflow-auto',
    // animations
    'data-[state=open]:pds-fade-in-subtle data-[state=open]:pds-animate-in data-[state=closed]:pds-animate-out data-[state=closed]:pds-fade-out-none data-[state=closed]:pds-zoom-out-95 data-[state=open]:pds-zoom-in-95 data-[state=closed]:pds-slide-out-to-bottom-[48%] data-[state=open]:pds-slide-in-from-bottom-[48%] md:data-[state=closed]:pds-slide-out-to-top-[48%] md:data-[state=open]:pds-slide-in-from-top-[48%]',
    // border radius styles
    'pds-rounded-t-xl md:pds-rounded-xl',
    'pds-max-h-[85vh] pds-w-screen pds-bg-surface pds-px-4 pds-py-5',
    'focus:pds-outline-none md:pds-w-[90vw] md:pds-max-w-[450px]',
  ],
  variants: {
    // Custom Variants
    isOpen: {
      false: 'pds-pointer-events-none pds-hidden',
      true: 'pds-block',
    },
  },
});

const dialogWrapperTv = pdsTv({
  base: [
    'pds-flex',
    'pds-w-screen',
    'pds-h-screen',
    'pds-fixed',
    'pds-inset-0',
    'pds-overflow-x-auto',
    'pds-justify-center',
    'pds-z-top',
    // positioning
    'pds-items-end md:pds-items-center',
  ],
});

export const HotspotDialog: React.FC<IInnerProps> = ({
  point,
  markerContent,
  markerMedia,
  componentName,
}) => {
  const [open, setOpen] = React.useState(false);
  const { name } = markerContent;
  const isSSR = typeof window === 'undefined';
  const arrowRef = React.useRef<SVGSVGElement>(null);
  const breakPoint = useBreakpoint();

  const { update, floatingStyles, refs, context } = useFloating({
    open,
    middleware: [
      offset(16),
      shift(),
      autoPlacement({ allowedPlacements: ['top', 'bottom', 'left', 'right'] }),
      arrow({ element: arrowRef }),
    ],
    strategy: 'absolute',
  });

  // Consider using inside onOpenChange
  useEffect(() => {
    if (!open) {
      document.body.style.pointerEvents = '';
      document.body.style.overflow = '';
    }

    if (open) {
      document.body.style.overflow = 'hidden';
    }
  }, [open]);

  // Close the dialog when the window is resized so portal recalculates correctly.
  useEffect(() => {
    const handleResize = () => {
      if (open) {
        setOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  // Update the floating element position when the reference or floating element changes.
  useEffect(() => {
    if (!open) return undefined;
    if (refs.reference.current && refs.floating.current) {
      return autoUpdate(refs.reference.current, refs.floating.current, update);
    }
    return undefined;
  }, [open, refs.reference, refs.floating, update]);

  return (
    <Dialog open={open} onOpenChange={setOpen} modal>
      <DialogTrigger asChild>
        <HotspotTrigger
          componentName={componentName}
          name={name}
          ref={refs.setReference}
          markerMedia={markerMedia}
          style={{
            left: `${point.x}%`,
            top: `${point.y}%`,
          }}
        />
      </DialogTrigger>
      {isSSR || !breakPoint.md ? (
        // Mobile Display using PDS-UI / forceMount for crawlers.
        <DialogContent
          forceMount
          className={dialogContentTv({ isOpen: open })}
          // @DEV - For some reason, onEscapeKeyDown not triggering internal state change for onOpenChange.
          onEscapeKeyDown={() => setOpen(false)}
        >
          <div className="pds-p-4">
            <button
              className="pds-absolute pds-right-4.5 pds-top-4.5 pds-z-oneBelowTop pds-rounded-circle pds-bg-surface pds-p-2"
              type="button"
              onClick={() => setOpen(false)}
            >
              <Icon
                className="pds-block"
                // @ts-ignore - width
                width={32}
                path={mdiClose}
              />
              <div className="pds-sr-only">Close</div>
            </button>
            <HotspotContent hotspot={markerContent} />
          </div>
        </DialogContent>
      ) : (
        // Custom Dialog using Radix-UI / Floating-UI for Popover-like behaviors.
        <DialogPrimitive.Portal>
          <div className={dialogWrapperTv()}>
            <DialogPrimitive.Content
              ref={refs.setFloating}
              className={dialogContentTv({ isOpen: open })}
              style={floatingStyles}
              // @DEV - For some reason, onEscapeKeyDown not triggering internal state change for onOpenChange.
              onEscapeKeyDown={() => setOpen(false)}
            >
              <FloatingArrow
                ref={arrowRef}
                context={context}
                width={20}
                className="pds-fill-surface"
              />
              <DialogPrimitive.Title className="pds-sr-only" asChild>
                <h3>{markerContent.name}</h3>
              </DialogPrimitive.Title>
              <div className="pds-p-4">
                {' '}
                <button
                  className="pds-absolute pds-right-4.5 pds-top-4.5 pds-z-oneBelowTop pds-rounded-circle pds-bg-surface pds-p-2"
                  type="button"
                  onClick={() => setOpen(false)}
                >
                  <Icon
                    className="pds-block"
                    // @ts-ignore - width
                    width={32}
                    path={mdiClose}
                  />
                  <div className="pds-sr-only">Close</div>
                </button>
                <HotspotContent hotspot={markerContent} />
              </div>
            </DialogPrimitive.Content>
          </div>
        </DialogPrimitive.Portal>
      )}
    </Dialog>
  );
};
